"use client";

import Dropdown from "@/components/Dropdown";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEventContext } from "@/context/EventContext";
import apiFetch from "@/lib/fetch";
import { useEffect, useState } from "react";
import CalendarDaysIcon from "@heroicons/react/24/solid/CalendarDaysIcon";
import { DROPDOWN_DEFAULT_VALUE_KEYS } from "@/@core/constants/dropdown";
import { useTranslation } from "@/hooks/translationContext";

export default function CalendarDropdown(props: SearchProps) {
  const { t } = useTranslation();
  // Built inside the component: at module scope there is no translator.
  const defaultService = { title: t("campaign-filter-service-placeholder"), id: 0 };
  const [selectedService, setSelectedService]: any = useState(defaultService);
  const [services, setServices] = useState([]);
  const [date, setDate] = useState<Date>(null);
  const { setEventFilters, eventFilters } = useEventContext();

  function ShowYear() {
    if (date) {
      const day = String(date?.getDate()).padStart(2, "0");
      const month = String(date?.getMonth() + 1).padStart(2, "0");
      const year = String(date?.getFullYear()).slice(-2);
      return `20${year}.${month}.${day}`;
    } else {
      return <></>;
    }
  }

  useEffect(() => {
    const commonParams = new URLSearchParams();
    if (props.service) commonParams.append("service", props.service);
    if (props.district) commonParams.append("district", props.district);

    if (eventFilters.date) {
      commonParams.append("date", eventFilters.date);
    }

    const getServices = async () => {
      const response = await apiFetch({
        route: `event/services?${commonParams.toString()}`,
      });
      setServices(response);
    };

    getServices();
  }, [eventFilters.date, props.service, props.district]);

  useEffect(() => {
    if (!eventFilters.service) {
      setSelectedService(defaultService);
    }

    if (!eventFilters.date) {
      setDate(null);
    }
  }, [eventFilters.service, eventFilters.date]);

  useEffect(() => {
    if (date) {
      const day = String(date?.getDate()).padStart(2, "0");
      const month = String(date?.getMonth() + 1).padStart(2, "0");
      const year = String(date?.getFullYear()).slice(-2);
      setEventFilters({
        date: `20${year}-${month}-${day}`,
        service: eventFilters.service,
      });
    } else {
      setEventFilters({
        date: null,
        service: eventFilters.service,
      });
    }
  }, [date]);

  useEffect(() => {
    setEventFilters({
      date: eventFilters.date,
      service: selectedService.id,
    });
  }, [selectedService]);

  return (
    <div className="flex md:justify-end md:flex-wrap gap-4 mt-4">
      <Popover>
        <PopoverTrigger asChild>
          <button
            role="button"
            className="flex gap-4 px-4 py-2 rounded-2xl border
                        bg-white items-center text-sm font-bold text-primary w-full md:w-[342px]"
          >
            <CalendarDaysIcon className="size-[36px]" />
            <div className="flex flex-col items-start font-bold text-sm">
              <span className="font-medium text-xs text-assistant">
                Tarih Seçiniz
              </span>
              <ShowYear />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            disabled={(date) => {
              const newDate = new Date();
              newDate.setHours(0, 0, 0, 0);

              return date < newDate;
            }}
            locale={{
              code: "tr-TR",
              localize: {
                month: (n: number) => {
                  const months: any = {
                    0: "Ocak",
                    1: t("calendar-month-february"),
                    2: "Mart",
                    3: "Nisan",
                    4: t("calendar-month-may"),
                    5: "Haziran",
                    6: "Temmuz",
                    7: t("calendar-month-august"),
                    8: t("calendar-month-september"),
                    9: "Ekim",
                    10: t("calendar-month-november"),
                    11: t("calendar-month-december"),
                  };

                  return months[n];
                },
                era: (n: number) => {
                  const eras: any = {
                    0: "MÖ",
                    1: "MS",
                  };

                  return eras[n];
                },
                quarter: (n: number) => {
                  const quarters: any = {
                    1: t("calendar-quarter-first"),
                    2: t("calendar-quarter-second"),
                    3: t("calendar-quarter-third"),
                    4: t("calendar-quarter-fourth"),
                  };

                  return quarters[n];
                },
                day: (n: number) => {
                  const days: any = {
                    0: "Pzr",
                    1: "Pzt",
                    2: "Sal",
                    3: t("calendar-day-wednesday-short"),
                    4: "Per",
                    5: "Cum",
                    6: "Cmt",
                  };

                  return days[n];
                },
                dayPeriod: (n: number) => {
                  const dayPeriods: any = {
                    0: "AM",
                    1: "PM",
                  };

                  return dayPeriods[n];
                },
                ordinalNumber: (n: number) => {
                  const ordinalNumbers: any = {
                    1: "1",
                    2: "2",
                    3: "3",
                    4: "4",
                    5: "5",
                    6: "6",
                    7: "7",
                    8: "8",
                    9: "9",
                    10: "10",
                    11: "11",
                    12: "12",
                    13: "13",
                    14: "14",
                    15: "15",
                    16: "16",
                    17: "17",
                    18: "18",
                    19: "19",
                    20: "20",
                    21: "21",
                    22: "22",
                    23: "23",
                    24: "24",
                    25: "25",
                    26: "26",
                    27: "27",
                    28: "28",
                    29: "29",
                    30: "30",
                    31: "31",
                  };

                  return ordinalNumbers[n];
                },
              },
              formatLong: {
                date: (n: any) => {
                  const date: any = {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  };

                  return n.toLocaleDateString("tr-TR", date);
                },
                dateTime: (n: any) => {
                  const dateTime: any = {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    timeZoneName: "short",
                  };

                  return n.toLocaleDateString("tr-TR", dateTime);
                },
                time: (n: any) => {
                  const time: any = {
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    timeZoneName: "short",
                  };

                  return n.toLocaleDateString("tr-TR", time);
                },
              },
            }}
            mode="single"
            selected={date}
            onSelect={(dt) => {
              setDate(dt as Date);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <Dropdown
        parentClassName="lg:!w-[278px] w-full"
        title={selectedService.title}
        className="w-full"
      >
        <button
          role="button"
          className="flex items-start w-full px-2 border-b border-stone-50 py-3 rounded cursor-pointer"
          onClick={() => {
            setSelectedService({
              title: t(DROPDOWN_DEFAULT_VALUE_KEYS.SERVICE),
              id: 0,
            });
          }}
        >
          <span className="text-sm text-slate-800 font-bold">Tümü</span>
        </button>

        {services.map((occ: { title: string; id: number }) => {
          return (
            <button
              role="button"
              key={occ.id}
              onClick={() => {
                setSelectedService({
                  title: occ.title,
                  id: occ.id,
                });
              }}
              className="w-full flex items-start px-2 border-b border-stone-50 font-bold py-3 rounded cursor-pointer"
            >
              <span className="text-sm text-slate-800 font-bold">
                {occ.title}
              </span>
            </button>
          );
        })}
      </Dropdown>
    </div>
  );
}
